import React, { useMemo } from 'react';
import { 
  Users, 
  UserCheck, 
  Timer,
  Target,
  Activity,
  PieChart
} from 'lucide-react';
import { Event } from '../../types/event';
import { useAppSelector } from '../../store/hooks';

interface EnhancedOverviewDashboardProps {
  event: Event;
  guestStats?: {
    total_invited: number;
    total_attendees: number;
    total_companions?: number;
    /** Main guests + companions, used as the denominator for the response bars. */
    total_invited_with_companions?: number;
    response_rate: number;
    status_breakdown: {
      pending: number;
      accepted: number;
      rejected: number;
      ignored: number;
    };
    /** Same breakdown for companions, so each response can be split guest vs companion. */
    companion_status_breakdown?: {
      pending: number;
      accepted: number;
      rejected: number;
      ignored: number;
    };
  };
  onQuickAction?: (action: string) => void;
}

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: {
    value: number;
    label: string;
    positive: boolean;
  };
  color: 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'indigo' | 'gray';
  size?: 'sm' | 'md' | 'lg';
  action?: {
    label: string;
    onClick: () => void;
  };
  progress?: {
    percentage: number;
    show: boolean;
  };
  theme: 'dark' | 'light';
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  color,
  size = 'md',
  action,
  progress,
  theme
}) => {

  return (
    <div className={`relative overflow-hidden rounded-xl shadow-lg border hover:shadow-xl transition-all duration-300 ease-out ${
      theme === 'dark' 
        ? 'bg-gray-900/80 border-gray-700/50' 
        : 'bg-white border-gray-200'
    }`}>
      {/* Theme-aware gradient background */}
      <div className={`absolute inset-0 ${
        theme === 'dark' 
          ? 'bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800' 
          : 'bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50'
      }`}></div>
      
      {/* Content */}
      <div className="relative z-10 p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-[#33FF00] to-[#2BD400] rounded-xl flex items-center justify-center shadow-md">
            <div className="text-black">
              {React.cloneElement(icon as React.ReactElement<any>, { className: 'h-6 w-6' })}
            </div>
          </div>
        </div>
        
        <div className="space-y-3">
          <h3 className={`text-sm font-semibold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>{title}</h3>
          <div className="flex items-baseline space-x-2">
            <span className={`text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {value}
            </span>
          </div>
          
          {subtitle && (
            <p className={`text-xs ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>{subtitle}</p>
          )}
          
          {/* Progress bar */}
          {progress?.show && (
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Progress</span>
                <span className={`font-medium ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>{Math.round(progress.percentage)}%</span>
              </div>
              <div className={`w-full rounded-full h-2 ${
                theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-200'
              }`}>
                <div 
                  className="bg-gradient-to-r from-[#33FF00] to-[#2BD400] h-2 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${Math.min(progress.percentage, 100)}%` }}
                />
              </div>
            </div>
          )}
          
          {action && (
            <button
              onClick={action.onClick}
              className={`mt-3 text-xs font-medium hover:text-[#2BD400] hover:underline focus:outline-none transition-colors ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}
            >
              {action.label} →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ProgressBar: React.FC<{
  label: string;
  /** Main guests with this response. */
  guestValue: number;
  /** Companions with this response (named plus count-only). */
  companionValue: number;
  total: number;
  color: string;
  showPercentage?: boolean;
  theme: 'dark' | 'light';
}> = ({ label, guestValue, companionValue, total, color, showPercentage = true, theme }) => {
  const value = guestValue + companionValue;
  const percentage = total > 0 ? (value / total) * 100 : 0;

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className={`text-sm font-medium ${
          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
        }`}>{label}</span>
        <span className={`text-sm text-right ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
        }`}>
          <span className={`font-semibold tabular-nums ${
            theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
          }`}>
            Total {value}
          </span>
          <span className="whitespace-nowrap">
            {' '}— {guestValue} guest{guestValue === 1 ? '' : 's'}, {companionValue} companion{companionValue === 1 ? '' : 's'}
          </span>
          {showPercentage && (
            <span className="tabular-nums"> ({Math.round(percentage)}%)</span>
          )}
        </span>
      </div>
      <div className={`w-full rounded-full h-2 ${
        theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-200'
      }`}>
        <div
          className={`h-2 rounded-full transition-all duration-300 ${color}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
};

export const EnhancedOverviewDashboard: React.FC<EnhancedOverviewDashboardProps> = ({
  event,
  guestStats,
  onQuickAction
}) => {
  const { theme } = useAppSelector((state) => state.ui);

  const stats = useMemo(() => {
    // Use guestStats if available, otherwise fall back to event data
    const totalAccepted = guestStats?.status_breakdown?.accepted || event.accepted_count || 0;
    const totalPending = guestStats?.status_breakdown?.pending || event.pending_count || 0;
    const totalRejected = guestStats?.status_breakdown?.rejected || event.rejected_count || 0;
    const totalIgnored = guestStats?.status_breakdown?.ignored || 0; // No event.ignored_count field
    
    // Calculate total invited as sum of all statuses
    const totalInvited = guestStats?.total_invited || (totalAccepted + totalPending + totalRejected + totalIgnored);
    
    // Calculate response rate properly: (responded / total_invited) * 100
    // Responded = accepted + rejected + ignored (any response counts)
    const totalResponded = totalAccepted + totalRejected + totalIgnored;
    const responseRate = totalInvited > 0 ? (totalResponded / totalInvited) * 100 : 0;

    // Companion half of each response, so the bars can read "80 guests, 20 companions".
    const companionBreakdown = guestStats?.companion_status_breakdown;
    const companionAccepted = companionBreakdown?.accepted || 0;
    const companionPending = companionBreakdown?.pending || 0;
    const companionRejected = companionBreakdown?.rejected || 0;
    const companionIgnored = companionBreakdown?.ignored || 0;

    // Capacity calculation: use total_attendees (includes companions) vs guest limit
    const totalAttendees = guestStats?.total_attendees || 0;
    const totalCompanions = guestStats?.total_companions || 0;

    // The response bars count companions, so their denominator has to as well - otherwise
    // a bar can exceed 100%.
    const totalInvitedWithCompanions =
      guestStats?.total_invited_with_companions ?? (totalInvited + totalCompanions);
    const capacityUsed = event.guest_limit ? (totalAttendees / event.guest_limit) * 100 : 0;
    const isOverCapacity = event.guest_limit ? totalAttendees > event.guest_limit : false;
    const isNearCapacity = !isOverCapacity && capacityUsed > 80;
    const isFull = event.guest_limit ? totalAttendees >= event.guest_limit : false;

    return {
      totalInvited,
      totalInvitedWithCompanions,
      totalAccepted,
      totalAttendees,
      totalCompanions,
      totalPending,
      totalRejected,
      totalIgnored,
      companionAccepted,
      companionPending,
      companionRejected,
      companionIgnored,
      totalResponded,
      responseRate,
      capacityUsed,
      isNearCapacity,
      isOverCapacity,
      isFull
    };
  }, [event, guestStats]);

  return (
    <div className="space-y-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Attending - Changed to show total attending (guests + companions) */}
        <StatCard
          title="Total Attending"
          value={stats.totalAttendees}
          subtitle={event.guest_limit ? `of ${event.guest_limit} capacity` : 'No limit set'}
          icon={<Users />}
          color={stats.isFull ? 'red' : stats.isNearCapacity ? 'yellow' : 'blue'}
          progress={event.guest_limit ? {
            percentage: (stats.totalAttendees / event.guest_limit) * 100,
            show: true
          } : undefined}
          theme={theme}
        />

        {/* Response Rate */}
        <StatCard
          title="Response Rate"
          value={`${Math.round(stats.responseRate)}%`}
          subtitle={`${stats.totalResponded || 0} responded`}
          icon={<Activity />}
          color={stats.responseRate >= 80 ? 'green' : stats.responseRate >= 60 ? 'yellow' : 'red'}
          trend={{
            value: Math.round(stats.responseRate * 100) / 100,
            label: 'response rate',
            positive: stats.responseRate >= 70
          }}
          progress={{
            percentage: stats.responseRate,
            show: true
          }}
          theme={theme}
        />

        {/* Total Guests - Changed to show total number of guests */}
        <StatCard
          title="Total Guests"
          value={stats.totalInvited}
          subtitle="Invited guests"
          icon={<UserCheck />}
          color={stats.totalInvited > 0 ? 'green' : 'gray'}
          progress={{
            percentage: stats.totalInvited > 0 ? 100 : 0,
            show: false
          }}
          theme={theme}
        />

        {/* Total Companions - Changed to show total number of companions */}
        <StatCard
          title="Total Companions"
          value={stats.totalCompanions}
          subtitle="Companions invited"
          icon={<UserCheck />}
          color={stats.totalCompanions > 0 ? 'green' : 'gray'}
          progress={{
            percentage: stats.totalCompanions > 0 ? 100 : 0,
            show: false
          }}
          theme={theme}
        />
      </div>

      {/* Detailed Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Guest Status Breakdown */}
        <div className={`rounded-xl border p-6 ${
          theme === 'dark' 
            ? 'bg-gray-900/80 border-gray-700/50' 
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center mb-6">
            <PieChart className={`h-5 w-5 mr-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`} />
            <h3 className={`text-lg font-semibold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>Guest Responses</h3>
          </div>
          
          <div className="space-y-4">
            <ProgressBar
              label="Accepted"
              guestValue={stats.totalAccepted}
              companionValue={stats.companionAccepted}
              total={stats.totalInvitedWithCompanions}
              color="bg-green-500"
              theme={theme}
            />
            <ProgressBar
              label="Pending"
              guestValue={stats.totalPending}
              companionValue={stats.companionPending}
              total={stats.totalInvitedWithCompanions}
              color="bg-yellow-500"
              theme={theme}
            />
            <ProgressBar
              label="Declined"
              guestValue={stats.totalRejected}
              companionValue={stats.companionRejected}
              total={stats.totalInvitedWithCompanions}
              color="bg-red-500"
              theme={theme}
            />
            {(stats.totalIgnored + stats.companionIgnored) > 0 && (
              <ProgressBar
                label="No Response"
                guestValue={stats.totalIgnored}
                companionValue={stats.companionIgnored}
                total={stats.totalInvitedWithCompanions}
                color="bg-gray-400"
                theme={theme}
              />
            )}
          </div>
        </div>

        {/* Capacity Status */}
        <div className={`rounded-xl border p-6 ${
          theme === 'dark' 
            ? 'bg-gray-900/80 border-gray-700/50' 
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center mb-6">
            <Target className={`h-5 w-5 mr-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`} />
            <h3 className={`text-lg font-semibold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>Capacity Status</h3>
          </div>

          {event.guest_limit ? (
            <div className="space-y-4">
              <div className="text-center">
                <div className={`text-3xl font-bold mb-2 ${
                  stats.isOverCapacity 
                    ? (theme === 'dark' ? 'text-red-400' : 'text-red-600')
                    : (theme === 'dark' ? 'text-white' : 'text-gray-900')
                }`}>
                  {Math.round(stats.capacityUsed)}%
                </div>
                <div className={`text-sm ${
                  stats.isOverCapacity
                    ? (theme === 'dark' ? 'text-red-300' : 'text-red-600')
                    : (theme === 'dark' ? 'text-gray-400' : 'text-gray-500')
                }`}>
                  {stats.totalAttendees} of {event.guest_limit} spots filled
                </div>
              </div>
              
              <div className={`w-full rounded-full h-4 ${
                theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-200'
              }`}>
                <div
                  className={`h-4 rounded-full transition-all duration-300 ${
                    stats.isOverCapacity ? 'bg-red-500' :
                    stats.isFull ? 'bg-red-500' : 
                    stats.isNearCapacity ? 'bg-yellow-500' : 
                    'bg-mirsal-green'
                  }`}
                  style={{ width: `${Math.min(stats.capacityUsed, 100)}%` }}
                />
              </div>

              <div className="flex justify-between text-sm">
                <span className={`font-medium ${
                  stats.isOverCapacity ? 'text-red-500' :
                  stats.isFull ? 'text-red-500' : 
                  stats.isNearCapacity ? 'text-yellow-500' : 
                  'text-green-500'
                }`}>
                  {stats.isOverCapacity ? 'Over Capacity' :
                   stats.isFull ? 'Full' : 
                   stats.isNearCapacity ? 'Near Capacity' : 
                   'Available'}
                </span>
                <span className={
                  stats.isOverCapacity
                    ? (theme === 'dark' ? 'text-red-400' : 'text-red-600')
                    : (theme === 'dark' ? 'text-gray-400' : 'text-gray-500')
                }>
                  {stats.isOverCapacity
                    ? `${stats.totalAttendees - event.guest_limit} over capacity`
                    : `${event.guest_limit - stats.totalAttendees} spots remaining`}
                </span>
              </div>

              {event.allow_companions && (
                <div className={`mt-4 p-3 rounded-lg ${
                  theme === 'dark' 
                    ? 'bg-blue-900/30 border border-blue-700/50' 
                    : 'bg-blue-50'
                }`}>
                  <p className={`text-sm ${
                    theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
                  }`}>
                    <UserCheck className="h-4 w-4 inline mr-1" />
                    Companions allowed - actual attendance may vary
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className={`mb-2 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
              }`}>
                <Users className="h-12 w-12 mx-auto" />
              </div>
              <p className={`text-lg font-medium mb-1 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>No Limit Set</p>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>Unlimited capacity for this event</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default EnhancedOverviewDashboard;
